import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface EmptyAllPhotosDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  photoCount: number;
  isLoading?: boolean;
}

export function EmptyAllPhotosDialog({
  open,
  onOpenChange,
  onConfirm,
  photoCount,
  isLoading = false,
}: EmptyAllPhotosDialogProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [confirmText, setConfirmText] = useState('');

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setStep(1);
      setConfirmText('');
    }
    onOpenChange(newOpen);
  };

  const handleContinue = () => {
    setStep(2);
  };

  const handleConfirm = () => {
    onConfirm();
    setStep(1);
    setConfirmText('');
  };

  const isConfirmValid = confirmText.trim().toLowerCase() === 'delete all';

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="border-destructive">
        {step === 1 ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-destructive">Delete All Photos?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete <span className="font-bold text-foreground">{photoCount} photo{photoCount !== 1 ? 's' : ''}</span> and all associated data including tags, colors, and metadata.
                <br /><br />
                <span className="font-semibold text-destructive">This action cannot be undone.</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleContinue();
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={isLoading}
              >
                Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-destructive">Confirm Deletion</AlertDialogTitle>
              <AlertDialogDescription>
                Type <span className="font-mono font-bold text-foreground">DELETE ALL</span> to confirm the permanent deletion of {photoCount} photo{photoCount !== 1 ? 's' : ''}.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <Label htmlFor="confirm-text" className="text-sm font-medium">
                Confirmation Code
              </Label>
              <Input
                id="confirm-text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE ALL"
                className={!isConfirmValid && confirmText.length > 0 ? 'border-destructive' : ''}
                disabled={isLoading}
                autoComplete="off"
              />
              {!isConfirmValid && confirmText.length > 0 && (
                <p className="text-xs text-destructive mt-1">
                  Please type exactly "DELETE ALL"
                </p>
              )}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => {
                  setStep(1);
                  setConfirmText('');
                }}
                disabled={isLoading}
              >
                Back
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleConfirm();
                }}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={!isConfirmValid || isLoading}
              >
                {isLoading ? 'Deleting...' : 'Delete All Photos'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
